import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import DashletContainer from 'Components/DashletContainer';
import { NumberService } from 'Services';
import { shouldShowMerchants, shouldShowReviewConclusions } from 'Selectors';
import clsx from 'clsx';
import { COLOR, FOCUS_VIEW } from 'Common/enums';
import styles from './AcceptRejectChartDashlet.css';

type Props = {
    className?: string; // container className
    header?: string;
    seriesData: PieChartSeriesData;
    isLoading?: boolean;
    totalLabel?: string;
    percentageValue?: number | string;
    singleMerchantId?: string;
    focusView: string;
    dataTestId: string;
    isCurrency?: boolean;
};

const AcceptedRejectedChartDashlet = ({
    className,
    header = '',
    seriesData,
    isLoading = false,
    totalLabel = '',
    percentageValue,
    singleMerchantId,
    focusView,
    dataTestId,
    isCurrency
}: Props) => {
    const containerElement = React.useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();
    if (!seriesData) return null;

    const containerWidth = Number(containerElement?.current?.clientWidth);

    const numFormatter = (value: number) => {
        return isCurrency ? NumberService.getLocaleCurrency(value) : NumberService.getLocaleNumber(value);
    };
    const merchantIdentifier =
        shouldShowMerchants() && (singleMerchantId ? `${t('MID')} ${singleMerchantId}` : t('All Merchants'));

    let color_val: string[] = [];
    if (focusView === FOCUS_VIEW.ACCEPT) {
        color_val = shouldShowReviewConclusions()
            ? [COLOR.GREEN, COLOR.DARK_GREY, COLOR.GREY]
            : [COLOR.GREEN, COLOR.GREY];
    } else if (focusView === FOCUS_VIEW.REJECT) {
        color_val = shouldShowReviewConclusions() ? [COLOR.RED, COLOR.DARK_GREY, COLOR.GREY] : [COLOR.RED, COLOR.GREY];
    } else if (focusView === FOCUS_VIEW.REVIEW) {
        color_val = [COLOR.BLUE, COLOR.DARK_GREY, COLOR.GREY];
    }

    const options = {
        chart: {
            plotBackgroundColor: null,
            plotBorderWidth: 0,
            plotShadow: false,
            height: 288
        },
        tooltip: {
            enabled: false
        },
        title: {
            floating: true,
            style: { 'font-family': 'var(--primaryFont)' },
            text: `<div style="font-size:40px">
            ${Number.isNaN(percentageValue) ? t('N/A') : `${percentageValue}%`}</div><br/>
            <div style="font-size:16px">${t(focusView)}</div>`,
            align: 'center',
            verticalAlign: 'middle',
            x: -(containerWidth * 0.23),
            y: 40
        },
        accessibility: {
            point: {
                valueSuffix: '%'
            }
        },
        plotOptions: {
            pie: {
                colors: color_val,
                dataLabels: {
                    enabled: false
                },
                showInLegend: true,
                center: ['25%', '50%'],
                point: {
                    events: {
                        legendItemClick() {
                            return false;
                        }
                    }
                }
            }
        },
        legend: {
            title: {
                text: merchantIdentifier,
                style: {
                    fontFamily: 'var(--boldFont)',
                    fontWeight: 'normal',
                    fontSize: '16px',
                    padding: '16px'
                }
            },
            verticalAlign: 'middle',
            layout: 'vertical',
            align: 'center',
            itemMarginBottom: 0,
            itemMarginTop: 0,
            useHTML: true,
            x: containerElement ? containerWidth * 0.25 : 100,
            symbolRadius: 0,
            symbolWidth: 0.1,
            symbolHeight: 0.1,
            symbolPadding: 0,
            labelFormatter() {
                const self: Record<string, any> = this;
                return `
                    <div class='${styles.labelHeader}'>
                        <div class='${styles.labelStyle}'>
                            <div class='${styles.legendIcon}' style='background: ${self.color}'></div>
                            <div>${t(self.name)}</div>
                        </div>
                        <div class='${styles.labelStyleValue}'>${numFormatter(self.y)}</div>
                    </div>    
                    <div style='${
                        self.index !== seriesData.length - 1 ? 'visiblity: hidden; height: 0px; line-height: 0;' : ''
                    }' class='${styles.labelHeader} ${styles.headerMarginTop}'>
                        <div class='${styles.labelStyle}'>
                            <div>${t(totalLabel)}</div>
                        </div>
                        <div class='${styles.labelStyleValue}'>${numFormatter(self.total)}</div>
                    </div>`;
            }
        },
        series: [
            {
                type: 'pie',
                innerSize: '75%',
                data: seriesData
            }
        ],
        credits: {
            enabled: false
        }
    };

    return (
        <DashletContainer
            key={`${isLoading}`}
            ref={containerElement}
            isLoading={isLoading}
            header={{ label: header }}
            className={clsx(className, styles.AcceptRejectChartDashlet)}
            dataTestId={dataTestId}
        >
            <HighchartsReact highcharts={Highcharts} options={options} />
        </DashletContainer>
    );
};

export default AcceptedRejectedChartDashlet;
